import React, { useCallback, useEffect, useRef, useState } from "react";

type UseGridRovingTabIndexOptions<T extends string> = {
  values: T[]; // the item values in the grid
  columnCount: number; // number of columns in the grid
  onActivate: (value: T) => void; // called when the user presses Enter or Space on an item
  onFinalArrowUp?: () => void; // up arrow on the last item in the grid
  onFinalArrowDown?: () => void;
};

type UseGridRovingTabIndexResult<T extends string> = {
  activeValue: T | undefined;
  setActiveValue: (value: T | undefined) => void;
  focusIndex: (index: number) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  handleContainerFocus: React.FocusEventHandler;
  handleContainerKeyDown: React.KeyboardEventHandler;
};

/**
 * Implements a roving tab index for a grid layout.
 *
 * Keyboard handling is attached once at the container level (for performance),
 * rather than creating onKeyDown/onFocus handlers for each option in the list.
 */
export function useGridRovingTabIndex<T extends string>({
  values,
  columnCount,
  onActivate,
  onFinalArrowUp,
  onFinalArrowDown,
}: UseGridRovingTabIndexOptions<T>): UseGridRovingTabIndexResult<T> {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeValue, setActiveValue] = useState<T | undefined>(values[0]);

  // if the list of `values` changes, then we may need to deselect the
  // activeValue if it's no longer available in the list.
  useEffect(() => {
    if (values.length === 0) {
      setActiveValue(undefined);
      return;
    }

    setActiveValue((current) => {
      if (current && values.includes(current)) {
        return current;
      }

      return values[0];
    });
  }, [values]);

  const focusIndex = useCallback(
    (nextIndex: number) => {
      if (values.length === 0) {
        return;
      }

      const clamped = Math.max(0, Math.min(nextIndex, values.length - 1));
      const value = values[clamped];

      setActiveValue(value);

      requestAnimationFrame(() => {
        const element = containerRef.current?.querySelector<HTMLElement>(
          `[data-option-value="${CSS.escape(value)}"]`,
        );

        element?.focus();
        element?.scrollIntoView({
          block: "nearest",
          inline: "nearest",
        });
      });
    },
    [values],
  );

  const handleContainerFocus = useCallback<React.FocusEventHandler>((event) => {
    const target = event.target as HTMLElement | null;
    const optionElement = target?.closest("[data-option-value]");

    const value = optionElement?.getAttribute("data-option-value") as T | null;

    if (!value) {
      return;
    }

    setActiveValue(value);
  }, []);

  const handleContainerKeyDown = useCallback<React.KeyboardEventHandler>(
    (event) => {
      if (values.length === 0) {
        return;
      }

      const index = getActiveIndex(values, activeValue);

      if (index === undefined) {
        return;
      }

      const column = index % columnCount;

      switch (event.key) {
        case "ArrowRight": {
          event.preventDefault();

          if (column < columnCount - 1 && index + 1 < values.length) {
            focusIndex(index + 1);
          }

          return;
        }

        case "ArrowLeft": {
          event.preventDefault();

          if (column > 0) {
            focusIndex(index - 1);
          }

          return;
        }

        case "ArrowDown": {
          event.preventDefault();

          const next = index + columnCount;

          if (next < values.length) {
            focusIndex(next);
          } else {
            onFinalArrowDown?.();
          }

          return;
        }

        case "ArrowUp": {
          event.preventDefault();

          const next = index - columnCount;

          if (next >= 0) {
            focusIndex(next);
          } else {
            onFinalArrowUp?.();
          }

          return;
        }

        case "Home": {
          event.preventDefault();
          focusIndex(0);
          return;
        }

        case "End": {
          event.preventDefault();
          focusIndex(values.length - 1);
          return;
        }

        case "Enter":
        case " ": {
          event.preventDefault();

          if (activeValue) {
            onActivate(activeValue);
          }

          return;
        }

        default:
          return;
      }
    },
    [
      activeValue,
      columnCount,
      focusIndex,
      values,
      onActivate,
      onFinalArrowDown,
      onFinalArrowUp,
    ],
  );

  return {
    activeValue,
    setActiveValue,
    focusIndex,
    containerRef,
    handleContainerFocus,
    handleContainerKeyDown,
  };
}

function getActiveIndex<T extends string>(
  values: T[],
  activeValue: T | undefined,
): number | undefined {
  if (values.length === 0) {
    return undefined;
  }

  if (!activeValue) {
    return 0;
  }

  const index = values.indexOf(activeValue);

  if (index >= 0) {
    return index;
  }

  return 0;
}
